import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import { IoMdClose } from "react-icons/io";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import debounce from 'lodash/debounce';
import CommonMrnFields from "../CommonMrnFields";

const MRNMDCreate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rows, setRows] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
  const [grandtotalinr, setGrandTotalInr] = useState(0);

  // Purchase Order Fields State
  const [poData, setPoData] = useState({
    podate: '',
    enquiryno: '',
    enquirydate: '',
    receivedate: '',
    pono: '',
    status: '',
    shipto: '',
    deliverynoteno: '',
    deliverynotedate: '',
    supplier: null,
    supplierDetails: {
      contactperson: '',
      contactno: '',
      email: ''
    },
    supplierMultipleAddress: null,
    branch: null,
    warehouse: null
  });

  const unitOptions = [
    { value: "", label: "Select UOM" },
    { value: "bot", label: "Bot" },
    { value: "ctn", label: "Ctn" },
    { value: "days", label: "Days" },
    { value: "ft", label: "Ft" },
    { value: "hrs", label: "Hrs" },
    { value: "kg", label: "Kg" },
    { value: "ltr", label: "Ltr" },
    { value: "mtr", label: "Mtr" },
    { value: "nos", label: "Nos" },
    { value: "pil", label: "Pil" },
    { value: "pkt", label: "Pkt" },
    { value: "set", label: "Set" },
    { value: "pcs", label: "Pcs" }
  ];

  const [mrndate, setmrndate] = useState('');
  const [jobnumber, setjobnumber] = useState("");

  // Fetch product data with debounced search
  const handleProductSearchChange = debounce((inputValue) => {
    setProductSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
  }, 500);

  const getPumpProductData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}product/page/ptype/0/10/desc/id/1/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
        { headers: authHeader() }
      );

      const products = response.data.Index || response.data.data || [];
      setProductOptions(
        products.map(product => ({
          value: product.id,
          label: `${product.description} - ${product.itemcode} - ${product.serialnumber} - ${product.moc} - ${product.partno}`,
          description: product.description,
          partno: product.partno,
          serialnumber: product.serialnumber,
          igst: product.igst,
          modelnumber: product.modelnumber,
          tagnumber: product.tagnumber,
          hsnsacno: product.hsnsacno,
          uom: product.unitofmeasurement || unitOptions[0].value,
          moc: product.moc || "",
          type: product.type || "",
          size: product.size || "",
          rating: product.rating || "",
          classes: product.classes || "",
          schedule: product.schedule || "",
          certification: product.certification || "",
        }))
      );
    } catch (error) {
      console.error("Error fetching products", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch Product. Please try again.",
      });
    }
  };

  // Handle product selection
  const handleProductChange = (selectedOption) => {
    setSelectedProduct(selectedOption);
    const selectedProductData = productOptions.find(product => product.value === selectedOption.value);
    if (selectedProductData) {
      const newRow = {
        product: { id: selectedOption.value, name: selectedOption.label },
        description: selectedProductData.description,
        partno: selectedProductData.partno,
        serialnumber: selectedProductData.serialnumber,
        hsnsacno: selectedProductData.hsnsacno,
        uom: selectedProductData.uom || unitOptions[0].value,
        moc: selectedProductData.moc || "",
        type: selectedProductData.type || "",
        size: selectedProductData.size || "",
        rating: selectedProductData.rating || "",
        classes: selectedProductData.classes || "",
        schedule: selectedProductData.schedule || "",
        certification: selectedProductData.certification || "",
        quantityreceived: '',
        ifanyremarks: "",
        mtrno: "",
        detailmaterialdescription: "",
        make: "",
        mfgsrno: "",
        heatnobatchno: "",
        platenosrno: ""
      };
      setRows([...rows, newRow]);
    }
  };

  // Handle row updates
  const handleRowChange = (index, key, value) => {
    const updatedRows = [...rows];
    updatedRows[index][key] = value;
    setRows(updatedRows);
  };

  const removeRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const validateForm = () => {
    if (!poData.supplier) {
      Swal.fire("Missing Supplier", "Please select a supplier before submitting the form.", "error");
      return false;
    }

    if (!poData.supplierMultipleAddress) {
      Swal.fire("Missing Address", "Please select the supplier's address.", "error");
      return false;
    }

    if (!poData.branch) {
      Swal.fire("Missing Branch", "Please select a branch.", "error");
      return false;
    }

    if (!poData.warehouse) {
      Swal.fire("Missing Warehouse", "Please select a warehouse.", "error");
      return false;
    }

    if (rows.length === 0) {
      Swal.fire("No Products Added", "Please add at least one product to continue.", "error");
      return false;
    }

    for (const row of rows) {
      if (!row.quantityreceived || isNaN(row.quantityreceived) || row.quantityreceived <= 0) {
        Swal.fire(
          "Invalid Quantity",
          "Each product must have a valid quantityreceived greater than 0.",
          "error"
        );
        return false;
      }
    }

    return true;
  };
  const handleSubmit = async () => {

    if (!validateForm()) return;

    setIsLoading(true);

    const materialReceiptNoteItems = rows.map((row) => ({
      product: { id: row.product.id },
      make: row.make || "",
      moc: row.moc || "",
      type: row.type || "",
      size: row.size || "",
      rating: row.rating || "",
      classes: row.classes || "",
      schedule: row.schedule || "",
      ifanyremarks: row.ifanyremarks || "",
      certification: row.certification || "",
      mtrno: row.mtrno || "",
      detailmaterialdescription: row.detailmaterialdescription || "",
      mfgsrno: row.mfgsrno || "",
      heatnobatchno: row.heatnobatchno || "",
      platenosrno: row.platenosrno || "",
      quantityreceived: row.quantityreceived || "",
    }));

    const mrnData = {
      materialReceiptNoteItems,
      mrntype: "Manufacturing",
      mrncategory: "Domestic",
      supplier: { id: poData.supplier?.value },
      contactno: poData.supplierDetails?.contactno,
      contactperson: poData.supplierDetails?.contactperson,
      email: poData.supplierDetails?.email,
      supplierMultipleAddress: { id: poData.supplierMultipleAddress?.value },
      branch: poData.branch ? { id: poData.branch?.value } : null,
      warehouse: poData.warehouse ? { id: poData.warehouse?.value } : null,
      podate: poData.podate,
      shipto: poData.shipto,
      deliverynotedate: poData.deliverynotedate,
      deliverynoteno: poData.deliverynoteno,
      jobnumber,
      mrndate,
      pono: poData.pono,
      receivedate: poData.receivedate,
      status: poData.status,
    };

    try {
      const response = await axios.post(
        BASE_URL + "materialReceiptNote/save",
        mrnData,
        {
          headers: authHeader(),
        }
      );
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "MRN added successfully!",
      });
      navigate("/mrnindex/1");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data || "Failed to add MRN. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getPumpProductData();
  }, [productSearch]);

  return (
    <div className="container">
      <br />
      <Form>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>GRN  Date</Form.Label>
              <Form.Control
                type="date"
                value={mrndate}
                onChange={e => setmrndate(e.target.value)}
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Job Number</Form.Label>
              <Form.Control
                type="text"
                value={jobnumber}
                onChange={(e) => setjobnumber(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <CommonMrnFields
          initialData={poData}
          onDataChange={setPoData}
        />

        <hr />
        <br />

        {/* Product Selection Section */}
        <Row>
          <Col md='12'>
            <Form.Group>
              <Form.Label>Select Product</Form.Label>
              <Select
                value={selectedProduct}
                onChange={handleProductChange}
                options={productOptions}
                placeholder="Search and select product"
                onInputChange={handleProductSearchChange}
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        <br />

        {/* Products Table */}
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Product Description</th>
                <th> If Any Remarks</th>
                <th>MTR No</th>
                <th>Detail Material Description</th>
                <th>Make</th>
                <th>Mfg. Sr. No</th>
                <th>Heat No / Batch No</th>
                <th>Plate No./ Sr. No</th>
                <th>Quantity Received</th>
                <th>Unit of Measurement</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>
                    {row.description}
                    <div style={{ marginTop: '10px' }}>
                      {[
                        { label: "MOC", value: row.moc, name: "moc" },
                        { label: "Type", value: row.type, name: "type" },
                        { label: "Size", value: row.size, name: "size" },
                        { label: "Rating", value: row.rating, name: "rating" },
                        { label: "Class", value: row.classes, name: "classes" },
                        { label: "Schedule", value: row.schedule, name: "schedule" },
                        { label: "Certification", value: row.certification, name: "certification" },
                      ].map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', marginBottom: '5px' }}>
                          <span style={{ minWidth: '100px' }}>{item.label}:</span>
                          <input
                            type="text"
                            value={item.value}
                            onChange={(e) => handleRowChange(index, item.name, e.target.value)}
                            size="sm"
                          />
                        </div>
                      ))}
                    </div>
                  </td>
                  <td>
                    <textarea
                      rows="2"
                      cols="30"
                      className="custom-textarea"
                      value={row.ifanyremarks}
                      onChange={(e) => handleRowChange(index, 'ifanyremarks', e.target.value)}
                      placeholder="Enter remarks"
                    />
                  </td>
                  <td>
                    <Form.Control
                      className="uom"
                      type="text"
                      value={row.mtrno}
                      onChange={(e) => handleRowChange(index, 'mtrno', e.target.value)}
                      placeholder="Enter MTR No"
                    />
                  </td>
                  <td>
                    <textarea
                      rows="2"
                      cols="30"
                      className="custom-textarea"
                      value={row.detailmaterialdescription}
                      onChange={(e) => handleRowChange(index, 'detailmaterialdescription', e.target.value)}
                      placeholder="Enter description"
                    />
                  </td>
                  <td>
                    <Form.Control
                      className="uom"
                      type="text"
                      value={row.make}
                      onChange={(e) => handleRowChange(index, 'make', e.target.value)}
                      placeholder="Enter make"
                    />
                  </td>
                  <td>
                    <Form.Control
                      className="uom"
                      type="text"
                      value={row.mfgsrno}
                      onChange={(e) => handleRowChange(index, 'mfgsrno', e.target.value)}
                      placeholder="Enter Mfg. Sr. No"
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      value={row.heatnobatchno}
                      onChange={(e) => handleRowChange(index, 'heatnobatchno', e.target.value)}
                      placeholder="Enter Heat/Batch No"
                    />
                  </td>
                  <td>
                    <Form.Control
                      className="uom"
                      type="text"
                      value={row.platenosrno}
                      onChange={(e) => handleRowChange(index, 'platenosrno', e.target.value)}
                      placeholder="Enter Plate/Sr. No"
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={row.quantityreceived}
                      onChange={(e) => handleRowChange(index, 'quantityreceived', e.target.value)}
                      placeholder="Enter quantity"
                      required
                    />
                  </td>
                  <td>
                    <Select
                      value={unitOptions.find(option => option.value === row.uom)}
                      onChange={(selected) => handleRowChange(index, 'uom', selected.value)}
                      options={unitOptions}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </td>
                  <td className="text-center">
                    <IoMdClose
                      style={{ cursor: 'pointer', fontSize: '1.5rem', color: 'red' }}
                      onClick={() => removeRow(index)}
                      title="Remove this item"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <br />

        {/* Submit Button */}
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Spinner as="span" animation="border" size="sm" />
              {' Submitting...'}
            </>
          ) : 'SUBMIT'}
        </button>
      </Form>
    </div>
  );
};

export default MRNMDCreate;
